// maths.portll.net — sign in / create account.
//
// One warm funnel: the OAuth providers from authProviders.js (Apple is flag-gated
// there, so this file never changes when it turns on) plus an email/password form that
// toggles between signing in and registering. We never navigate imperatively on the
// auth call — a useEffect forwards to /invite once the shared AuthContext actually has
// a user, which sidesteps the race where RequireAuth would read a still-null user and
// bounce back here. The route guards take it onward from /invite.

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { OAUTH_PROVIDERS } from "../lib/authProviders.js";
import { signInWithEmail, registerWithEmail, resetPassword } from "../lib/firebase.js";
import { useAuth } from "../contexts/AuthContext.jsx";
import { Button, Card, Field, Note, Spinner, injectStyles } from "../components/ui.jsx";

// Firebase auth error codes → warm, blame-free copy. Returns "" for user-cancelled
// popups so we stay silent instead of scolding someone who changed their mind.
function friendlyAuthError(err) {
  switch (err?.code) {
    case "auth/invalid-email":
      return "That doesn't look like an email address.";
    case "auth/missing-password":
      return "Please pop in your password.";
    case "auth/weak-password":
      return "Please choose a password with at least 6 characters.";
    case "auth/email-already-in-use":
      return "There's already an account with that email — try signing in instead.";
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "That email and password don't match. Have another go, or reset your password below.";
    case "auth/user-disabled":
      return "This account has been paused. Get in touch and we'll sort it out.";
    case "auth/too-many-requests":
      return "That's a lot of tries. Wait a moment, then try again.";
    case "auth/network-request-failed":
      return "Can't reach the network — check your connection and try again.";
    case "auth/popup-blocked":
      return "Your browser blocked the sign-in window. Allow pop-ups and try again.";
    case "auth/account-exists-with-different-credential":
      return "You've signed in before with a different method. Try Google, or your email and password.";
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return ""; // deliberately cancelled — no error to show
    default:
      return err?.message || "Something went wrong. Please try again.";
  }
}

export default function Login() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState("signin"); // "signin" | "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");

  // Forward once the shared context truly has a user (not before) → no guard bounce.
  useEffect(() => {
    if (user) navigate("/invite", { replace: true });
  }, [user, navigate]);

  const isRegister = mode === "register";

  function clearMessages() {
    if (err) setErr("");
    if (info) setInfo("");
  }

  async function handleOAuth(provider) {
    if (busy) return;
    clearMessages();
    setBusy(true);
    try {
      await provider.signIn();
      // navigation handled by the useEffect above once `user` propagates
    } catch (e) {
      const msg = friendlyAuthError(e);
      if (msg) setErr(msg);
      setBusy(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (busy) return;
    clearMessages();
    const em = email.trim();
    if (!em || !password) {
      setErr("Please enter your email and password.");
      return;
    }
    setBusy(true);
    try {
      if (isRegister) await registerWithEmail(em, password);
      else await signInWithEmail(em, password);
      // navigation handled by the useEffect above once `user` propagates
    } catch (e2) {
      setErr(friendlyAuthError(e2));
      setBusy(false);
    }
  }

  async function handleReset() {
    if (busy) return;
    const em = email.trim();
    if (!em) {
      setInfo("");
      setErr("Type your email above first, then tap “Forgot password?”.");
      return;
    }
    clearMessages();
    setBusy(true);
    try {
      await resetPassword(em);
      setInfo("If that email has an account, a reset link is on its way. Check your inbox.");
    } catch (e) {
      setErr(friendlyAuthError(e));
    } finally {
      setBusy(false);
    }
  }

  function toggleMode() {
    clearMessages();
    setMode(isRegister ? "signin" : "register");
  }

  return (
    <main className="au-shell">
      <Link to="/" className="au-brand" aria-label="maths.portll.net home">
        <span aria-hidden="true">🌿</span>
        <span>maths<span className="au-brand__dot">·</span>portll</span>
      </Link>

      <Card className="au-card">
        <div className="au-head">
          <h1 className="au-title">{isRegister ? "Create your account" : "Welcome back"}</h1>
          <p className="au-sub">
            {isRegister
              ? "One grown-up account looks after the whole family."
              : "Sign in to open the Grown-Ups’ Corner."}
          </p>
        </div>

        {OAUTH_PROVIDERS.length > 0 && (
          <>
            <div className="au-oauth">
              {OAUTH_PROVIDERS.map((p) => (
                <Button
                  key={p.id}
                  variant="secondary"
                  className="au-full"
                  onClick={() => handleOAuth(p)}
                  disabled={busy}
                >
                  {p.label}
                </Button>
              ))}
            </div>
            <div className="au-or" role="separator" aria-label="or continue with email">
              <span>or</span>
            </div>
          </>
        )}

        <form className="au-form" onSubmit={handleSubmit} noValidate>
          <Field label="Email" htmlFor="au-email">
            <input
              id="au-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck="false"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                clearMessages();
              }}
              disabled={busy}
            />
          </Field>

          <Field
            label="Password"
            htmlFor="au-pw"
            hint={isRegister ? "At least 6 characters." : undefined}
          >
            <div className="au-pw">
              <input
                id="au-pw"
                type={showPw ? "text" : "password"}
                autoComplete={isRegister ? "new-password" : "current-password"}
                placeholder={isRegister ? "Choose a password" : "Your password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  clearMessages();
                }}
                disabled={busy}
              />
              <button
                type="button"
                className="au-pw__toggle"
                onClick={() => setShowPw((v) => !v)}
                aria-pressed={showPw}
                aria-label={showPw ? "Hide password" : "Show password"}
                disabled={busy}
              >
                {showPw ? "Hide" : "Show"}
              </button>
            </div>
          </Field>

          {err && <Note tone="danger">{err}</Note>}
          {info && <Note tone="brand">{info}</Note>}

          <Button type="submit" variant="primary" size="lg" className="au-full" disabled={busy}>
            {busy ? (
              <>
                <Spinner size={18} label="Working" /> Just a moment…
              </>
            ) : isRegister ? (
              "Create account"
            ) : (
              "Sign in"
            )}
          </Button>
        </form>

        <div className="au-links">
          {!isRegister && (
            <button type="button" className="au-link" onClick={handleReset} disabled={busy}>
              Forgot password?
            </button>
          )}
          <button type="button" className="au-link" onClick={toggleMode} disabled={busy}>
            {isRegister ? "Already have an account? Sign in" : "New here? Create an account"}
          </button>
        </div>
      </Card>

      <p className="au-foot no-print">
        <Link to="/">← Just looking? See what’s inside</Link>
      </p>
    </main>
  );
}

const AU_CSS = `
.au-shell{min-height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;
  gap:1.1rem;padding:clamp(1rem,4vw,2.5rem);}
.au-brand{display:inline-flex;align-items:center;gap:.45rem;text-decoration:none;color:var(--brand-ink);
  font-weight:800;font-size:1.15rem;letter-spacing:-.01em;}
.au-brand__dot{color:var(--accent);}
.au-card{width:100%;max-width:26rem;padding:1.4rem 1.4rem 1.2rem;}
.au-head{text-align:center;margin-bottom:1.1rem;}
.au-title{margin:0;font-size:clamp(1.35rem,4.5vw,1.7rem);}
.au-sub{margin:.35rem 0 0;color:var(--ink-soft);}
.au-oauth{display:flex;flex-direction:column;gap:.6rem;}
.au-full{width:100%;}
.au-or{display:flex;align-items:center;gap:.75rem;margin:1rem 0;color:var(--ink-soft);font-size:.85rem;}
.au-or::before,.au-or::after{content:"";flex:1 1 auto;height:1px;background:var(--line);}
.au-form{display:flex;flex-direction:column;gap:.2rem;}
.au-form .ui-field{margin-bottom:.7rem;}
.au-pw{position:relative;}
.au-pw input{padding-right:4.4rem;}
.au-pw__toggle{position:absolute;top:50%;right:.35rem;transform:translateY(-50%);min-height:40px;
  padding:0 .7rem;background:transparent;border:none;border-radius:8px;color:var(--sky);
  font-weight:600;font-size:.9rem;}
.au-pw__toggle:active{transform:translateY(-50%) scale(.97);}
.au-links{display:flex;flex-wrap:wrap;justify-content:space-between;gap:.5rem 1rem;margin-top:1rem;}
.au-link{background:transparent;border:none;padding:.4rem .1rem;min-height:44px;color:var(--sky);
  font-weight:600;font-size:.92rem;text-align:left;}
.au-link:disabled{opacity:.5;}
.au-foot{margin:0;font-size:.9rem;}
.au-foot a{color:var(--ink-soft);text-decoration:none;font-weight:600;}
`;

injectStyles("maths-login-styles", AU_CSS);
